import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useCategories } from "../../lib/categoriesContext";
import { TEMPLATE_LABELS } from "../../lib/categories";
import { Logo } from "../../components/Logo";
import { Plus, LogOut, Edit, Trash2, ExternalLink, FolderTree, ShoppingBag, FileText, Mail, BookOpen, UserCog, FileEdit, Users } from "lucide-react";
import { MaintenanceToggle } from "../../components/MaintenanceToggle";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { categories } = useCategories();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => { if (user === false) navigate("/admin/login", { replace: true }); }, [user, navigate]);

  const fetchArticles = () => {
    setLoading(true);
    const params = filter === "all" ? {} : { status: filter };
    api.get("/admin/articles", { params }).then((r) => setArticles(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { if (user && (user.role === "admin" || user.role === "editor")) fetchArticles();
    // eslint-disable-next-line
  }, [user, filter]);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Supprimer "${title}" ?`)) return;
    await api.delete(`/admin/articles/${id}`);
    fetchArticles();
  };

  const catName = (slug) => categories.find((c) => c.slug === slug)?.name || slug;

  if (!user || user === null) return <div className="py-32 text-center font-mono-label">CHARGEMENT…</div>;

  return (
    <div className="min-h-screen bg-[#FAF8F3]" data-testid="admin-dashboard">
      <header className="border-b-2 border-[#1A1A18] bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" data-testid="admin-back-home"><Logo size="sm" /></Link>
            <span className="font-mono-label text-[#4A7A3D] hidden md:inline">CMS · ESPACE RÉDACTION</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-mono-label text-[#4B4B48] hidden md:inline">{user.email}</span>
            <Link to="/admin/profile" className="font-mono-label flex items-center gap-2 text-[#1A1A18] hover:text-[#4A7A3D]" data-testid="admin-profile-link">
              <UserCog size={14} /> <span className="hidden sm:inline">Mon compte</span>
            </Link>
            <button onClick={logout} className="font-mono-label flex items-center gap-2 text-[#1A1A18] hover:text-[#4A7A3D]" data-testid="admin-logout">
              <LogOut size={14} /> <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Site mode (maintenance toggle) — admin only */}
        {user.role === "admin" && <MaintenanceToggle />}

        {/* Quick links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 mb-10">
          <Link to="/admin/articles/nouveau" className="bg-[#4A7A3D] text-[#FAF8F3] p-5 hover:bg-[#3E6633] flex items-center gap-3 group" data-testid="admin-quick-new-article">
            <Plus size={28} />
            <div>
              <div className="font-display text-2xl">Nouvel article</div>
              <div className="font-mono-label text-[#FAF8F3]/80">11 templates</div>
            </div>
          </Link>
          {user.role === "admin" && (
            <Link to="/admin/rubriques" className="border-2 border-[#1A1A18] p-5 hover:bg-[#F5EFEB] flex items-center gap-3" data-testid="admin-quick-categories">
              <FolderTree size={24} className="text-[#4A7A3D]" />
              <div>
                <div className="font-display text-xl">Rubriques</div>
                <div className="font-mono-label text-[#4B4B48]">{categories.length} actives</div>
              </div>
            </Link>
          )}
          <Link to="/admin/boutique" className="border-2 border-[#1A1A18] p-5 hover:bg-[#F5EFEB] flex items-center gap-3" data-testid="admin-quick-products">
            <ShoppingBag size={24} className="text-[#4A7A3D]" />
            <div>
              <div className="font-display text-xl">Boutique</div>
              <div className="font-mono-label text-[#4B4B48]">Produits</div>
            </div>
          </Link>
          <Link to="/admin/messages" className="border-2 border-[#1A1A18] p-5 hover:bg-[#F5EFEB] flex items-center gap-3" data-testid="admin-quick-messages">
            <Mail size={24} className="text-[#C4944A]" />
            <div>
              <div className="font-display text-xl">Messages</div>
              <div className="font-mono-label text-[#4B4B48]">Boîte de réception</div>
            </div>
          </Link>
          <Link to="/admin/ressources" className="border-2 border-[#1A1A18] p-5 hover:bg-[#F5EFEB] flex items-center gap-3" data-testid="admin-quick-resources">
            <BookOpen size={24} className="text-[#4A7A3D]" />
            <div>
              <div className="font-display text-xl">Ressources</div>
              <div className="font-mono-label text-[#4B4B48]">Liens utiles</div>
            </div>
          </Link>
          <Link to="/admin/pages" className="border-2 border-[#1A1A18] p-5 hover:bg-[#F5EFEB] flex items-center gap-3" data-testid="admin-quick-pages">
            <FileEdit size={24} className="text-[#C4944A]" />
            <div>
              <div className="font-display text-xl">Pages</div>
              <div className="font-mono-label text-[#4B4B48]">Textes & images</div>
            </div>
          </Link>
          {user.role === "admin" && (
            <Link to="/admin/utilisateurs" className="border-2 border-[#1A1A18] p-5 hover:bg-[#F5EFEB] flex items-center gap-3" data-testid="admin-quick-users">
              <Users size={24} className="text-[#4A7A3D]" />
              <div>
                <div className="font-display text-xl">Utilisateurs</div>
                <div className="font-mono-label text-[#4B4B48]">Comptes & rôles</div>
              </div>
            </Link>
          )}
        </div>

        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <div className="font-mono-label text-[#4A7A3D] mb-2 flex items-center gap-2"><FileText size={14} /> Articles</div>
            <h1 className="font-display text-4xl md:text-5xl leading-[1.0]">Tableau de bord</h1>
          </div>
        </div>

        <div className="flex gap-2 mb-8">
          {[{ v: "all", l: "Tous" }, { v: "published", l: "Publiés" }, { v: "draft", l: "Brouillons" }].map((f) => (
            <button key={f.v} onClick={() => setFilter(f.v)} className={`font-mono-label px-4 py-2 border-2 ${filter === f.v ? "bg-[#1A1A18] text-[#FAF8F3] border-[#1A1A18]" : "border-[#E0D8CE] hover:border-[#1A1A18]"}`} data-testid={`admin-filter-${f.v}`}>
              {f.l}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="font-mono-label">CHARGEMENT…</p>
        ) : articles.length === 0 ? (
          <div className="py-16 border-2 border-dashed border-[#E0D8CE] text-center">
            <p className="font-quote text-xl text-[#4B4B48] mb-4">Aucun article {filter !== "all" ? `(${filter})` : ""} pour le moment.</p>
            <Link to="/admin/articles/nouveau" className="bg-[#4A7A3D] text-[#FAF8F3] px-6 py-3 font-mono-label inline-flex items-center gap-2">
              <Plus size={16} /> Créer le premier article
            </Link>
          </div>
        ) : (
          <div className="border-2 border-[#1A1A18]">
            <div className="hidden md:grid grid-cols-12 bg-[#1A1A18] text-[#FAF8F3] font-mono-label py-3 px-4">
              <div className="col-span-5">Titre</div>
              <div className="col-span-2">Rubrique</div>
              <div className="col-span-1">Format</div>
              <div className="col-span-2">Statut</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {articles.map((a) => (
              <div key={a.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 py-4 px-4 border-t border-[#E0D8CE] items-center" data-testid={`admin-article-${a.id}`}>
                <div className="md:col-span-5">
                  <div className="font-display text-lg leading-tight">{a.title}</div>
                  <div className="text-xs text-[#4B4B48]">{a.subtitle?.slice(0, 80)}</div>
                </div>
                <div className="md:col-span-2 font-mono-label text-[#4A7A3D]">{catName(a.category_slug)}</div>
                <div className="md:col-span-1 font-mono-label text-[#4B4B48] text-[10px]">{TEMPLATE_LABELS[a.template] || a.template}</div>
                <div className="md:col-span-2">
                  <span className={`font-mono-label px-2 py-1 ${a.status === "published" ? "bg-[#8FAF82]/30 text-[#4A7A3D]" : "bg-[#C4944A]/30 text-[#1A1A18]"}`}>
                    {a.status === "published" ? "Publié" : "Brouillon"}
                  </span>
                </div>
                <div className="md:col-span-2 flex md:justify-end gap-2">
                  {a.status === "published" && (
                    <Link to={`/article/${a.slug}`} className="p-2 hover:bg-[#F5EFEB] border border-[#E0D8CE]" title="Voir" data-testid={`admin-view-${a.id}`}>
                      <ExternalLink size={14} />
                    </Link>
                  )}
                  <Link to={`/admin/articles/${a.id}`} className="p-2 hover:bg-[#F5EFEB] border border-[#E0D8CE]" title="Éditer" data-testid={`admin-edit-${a.id}`}>
                    <Edit size={14} />
                  </Link>
                  <button onClick={() => handleDelete(a.id, a.title)} className="p-2 hover:bg-red-100 border border-[#E0D8CE]" title="Supprimer" data-testid={`admin-delete-${a.id}`}>
                    <Trash2 size={14} className="text-red-700" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
